/**
 * Session Lock Redux Slice
 * 
 * Manages the state for session locking functionality.
 * Includes states for lock conflicts, expiration warnings, and acquisition errors.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface SessionLockConflictData {
  isLocked: boolean;
  alreadyLocked: boolean;
  lockedBy: string;
  lockedByName: string;
  lockedAt: string;
  expiresAt: string;
  resourceType?: string;
}

export interface LockExpirationWarningData {
  resourceId: string;
  expiresAt: string;
  lockToken: string;
}

export interface LockAcquisitionErrorData {
  error: string;
  lockRequest?: any;
}

export interface SessionLockState {
  isLockConflictModalVisible: boolean;
  lockConflictData: SessionLockConflictData | null;
  isLockExpirationWarningVisible: boolean;
  lockExpirationWarningData: LockExpirationWarningData | null;
  isLockAcquisitionErrorVisible: boolean;
  lockAcquisitionErrorData: LockAcquisitionErrorData | null;
  activeLocks: Record<string, any>;
  lockErrors: string | null;
}

const initialState: SessionLockState = {
  isLockConflictModalVisible: false,
  lockConflictData: null,
  isLockExpirationWarningVisible: false,
  lockExpirationWarningData: null,
  isLockAcquisitionErrorVisible: false,
  lockAcquisitionErrorData: null,
  activeLocks: {},
  lockErrors: null,
};

const sessionLockSlice = createSlice({
  name: 'sessionLock',
  initialState,
  reducers: {
    showSessionLockConflictModal(state, action: PayloadAction<SessionLockConflictData>) {
      console.log('🔴 REDUCER: showSessionLockConflictModal called with:', action.payload);
      state.lockConflictData = action.payload;
      state.isLockConflictModalVisible = true;
    },
    hideSessionLockConflictModal(state) {
      state.isLockConflictModalVisible = false;
      state.lockConflictData = null;
    },
    showLockExpirationWarning(state, action: PayloadAction<LockExpirationWarningData>) {
      console.log('🔴 REDUCER: showLockExpirationWarning called with:', action.payload);
      state.lockExpirationWarningData = action.payload;
      state.isLockExpirationWarningVisible = true;
      console.log('🔴 REDUCER: State updated. New state:', {
        isLockExpirationWarningVisible: state.isLockExpirationWarningVisible,
        lockExpirationWarningData: state.lockExpirationWarningData,
      });
    },
    hideLockExpirationWarning(state) {
      state.isLockExpirationWarningVisible = false;
      state.lockExpirationWarningData = null;
    },
    showLockAcquisitionError(state, action: PayloadAction<LockAcquisitionErrorData>) {
      state.lockAcquisitionErrorData = action.payload;
      state.isLockAcquisitionErrorVisible = true;
    },
    hideLockAcquisitionError(state) {
      state.isLockAcquisitionErrorVisible = false;
      state.lockAcquisitionErrorData = null;
    },
    setActiveLock(state, action: PayloadAction<{ resourceId: string; lockData: any }>) {
      state.activeLocks[action.payload.resourceId] = action.payload.lockData;
    },
    clearActiveLock(state, action: PayloadAction<string>) {
      delete state.activeLocks[action.payload];
    },
    setLockErrors(state, action: PayloadAction<string | null>) {
      state.lockErrors = action.payload;
    },
  },
});

export const {
  showSessionLockConflictModal,
  hideSessionLockConflictModal,
  showLockExpirationWarning,
  hideLockExpirationWarning,
  showLockAcquisitionError,
  hideLockAcquisitionError,
  setActiveLock,
  clearActiveLock,
  setLockErrors,
} = sessionLockSlice.actions;

// Selector for active locks
export const selectActiveLocks = (state: any) => state?.sessionLock?.activeLocks || {};

export default sessionLockSlice.reducer;
